/* ── BR Scatter Splash ────────────────────────────────────────────
 * Spread-out particle splash from the swarm centroid for the Scatter
 * card. Particles fan outward in a hemisphere with strong horizontal
 * spread so it reads as "spread the swarm".
 *
 * Lifecycle scaffolding lives in CardSignatureEffect.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import { Fn, uniform, vec3, vec4, float } from 'three/tsl';
import { setEmissiveNode } from '../tslUtils';
import { CardSignatureEffect, type LivePositionResolver, type Vec3Like } from './CardSignatureEffect';

const LIFE_SECONDS = 0.9;
const PARTICLE_COUNT = 48;
const COLOR_HEX = 0xff8a3b;
const SCATTER_SPEED = 8;

interface BrScatterSplashOptions {
  liveResolver?: LivePositionResolver;
}

export class BrScatterSplash extends CardSignatureEffect {
  static readonly LIFE_MS = LIFE_SECONDS * 1000;

  private readonly points: THREE.Points;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.PointsNodeMaterial;
  private readonly positions: Float32Array;
  private readonly velocities: Float32Array;
  private readonly uProgress: ReturnType<typeof uniform>;
  private readonly liveResolver?: LivePositionResolver;
  private readonly initialOrigin = new THREE.Vector3();

  constructor(scene: THREE.Scene, position: Vec3Like, options?: BrScatterSplashOptions) {
    super(scene, LIFE_SECONDS);
    this.liveResolver = options?.liveResolver;
    this.initialOrigin.set(position.x, position.y, position.z);

    this.uProgress = uniform(0);
    const uColor = uniform(new THREE.Color(COLOR_HEX));
    const uProgress = this.uProgress;

    this.positions = new Float32Array(PARTICLE_COUNT * 3);
    this.velocities = new Float32Array(PARTICLE_COUNT * 3);

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const i3 = i * 3;
      this.positions[i3] = position.x;
      this.positions[i3 + 1] = position.y;
      this.positions[i3 + 2] = position.z;
      // Hemisphere fan: theta full, phi biased toward XZ horizon (small Y).
      const theta = (i / PARTICLE_COUNT) * Math.PI * 2;
      const phi = (Math.random() * 0.4 + 0.3) * Math.PI; // 54..126° from +Y
      const speed = SCATTER_SPEED * (0.7 + Math.random() * 0.6);
      this.velocities[i3] = Math.sin(phi) * Math.cos(theta) * speed;
      this.velocities[i3 + 1] = Math.cos(phi) * speed * 0.4 + 1.5;
      this.velocities[i3 + 2] = Math.sin(phi) * Math.sin(theta) * speed;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));

    this.material = new THREE.PointsNodeMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const sizeNode = Fn(() => {
      const shrink = float(1.0).sub(float(uProgress).mul(0.6));
      return float(40.0).mul(shrink);
    })();
    const colorNode = Fn(() => {
      const fade = float(uProgress).oneMinus();
      const tint = vec3(uColor);
      const rgb = tint.add(vec3(0.4, 0.2, 0.0).mul(fade));
      return vec4(rgb, fade);
    })();
    this.material.sizeNode = sizeNode;
    this.material.colorNode = colorNode;
    setEmissiveNode(this.material, colorNode);

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    scene.add(this.points);
  }

  protected tick(t: number, dtSec: number): void {
    this.uProgress.value = t;
    this.applyLiveDelta(this.points, this.liveResolver, this.initialOrigin);
    const drag = 1 - t * 0.5;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const i3 = i * 3;
      this.positions[i3] += this.velocities[i3] * dtSec * drag;
      this.positions[i3 + 1] += (this.velocities[i3 + 1] - 4 * t) * dtSec * drag;
      this.positions[i3 + 2] += this.velocities[i3 + 2] * dtSec * drag;
    }
    (this.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
  }

  protected disposeResources(): void {
    this.scene.remove(this.points);
    this.geometry.dispose();
    this.material.dispose();
  }
}
